import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

function Register() {
  // State variables to store form data and UI states
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get auth context
  const { register, loading: authLoading, error: authError } = useAuth();

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update the userData state with the new value
    setUserData({
      ...userData, // Keep all existing values
      [name]: value // Update only the changed field
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission behavior
    
    // Validate form inputs
    if (!userData.name || !userData.email || !userData.username || !userData.password) {
      setLocalError('All fields are required');
      return;
    }
    
    if (userData.password !== userData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    
    if (userData.password.length < 6) {
      setLocalError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setLocalLoading(true);
      setLocalError(null);
      
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...registrationData } = userData;
      
      // Call the register function from AuthContext
      const result = await register(registrationData);
      
      if (result.success) {
        // Handle successful registration
        setSuccessMessage('Registration successful! You can now log in.');
        
        // Clear the form
        setUserData({
          name: '',
          email: '',
          username: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        // Handle registration failure
        setLocalError(result.error || 'Registration failed. Please try again.');
      }
      
    } catch (err) {
      // Handle unexpected errors
      console.error('Registration failed:', err);
      setLocalError('An unexpected error occurred. Please try again.');
    } finally {
      setLocalLoading(false);
    }
  };

  // Determine if we're in a loading state
  const isLoading = localLoading || authLoading;
  
  // Show auth error if available, otherwise show local error
  const errorMessage = authError || localError;

  return (
    <div className="register-container">
      <div className="card">
        <div className="card-header">
          <h2>Create an Account</h2>
        </div>
        <div className="card-body">
          {/* Show error message if there is one */}
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          
          {/* Show success message if there is one */}
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
          
          {/* Registration form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={userData.name}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email address</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={userData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              <div className="form-text">We'll never share your email with anyone else.</div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="username" className="form-label">Username</label>
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                value={userData.username}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                value={userData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              <div className="form-text">Password must be at least 6 characters long.</div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                name="confirmPassword"
                value={userData.confirmPassword}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Register'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register; 